import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransporterForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('auth.enterEmail'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API}/transporters/password-reset/request`, { 
        email: email.toLowerCase(),
        source: "web"
      });
      setSuccess(true);
    } catch (err) {
      const message = err.response?.data?.detail || t('errors.submitFailed');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-md mx-auto px-4 relative z-10">
          <button
            onClick={() => navigate('/drivers/login')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.backToLogin')}</span>
          </button>

          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">{t('auth.forgotPassword')}</h1>
              <p className="text-gray-400">{t('auth.forgotPasswordSubtitle')}</p>
            </div>

            {success ? (
              <div className="text-center">
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-white mb-3">{t('auth.resetEmailSent')}</h2>
                <p className="text-gray-400 mb-6">
                  {t('auth.checkEmailForReset')}
                </p>
                <Link
                  to="/drivers/login"
                  className="text-amber-500 font-semibold hover:text-amber-400 transition-colors"
                >
                  {t('common.backToLogin')} →
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                    <p className="text-red-500 text-sm text-center">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
                  <div>
                    <Label className="text-gray-300 mb-2 block">{t('auth.email')}</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                      data-testid="email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg"
                    data-testid="reset-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t('common.sending')}
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        {t('auth.sendResetLink')}
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-white/10 text-center">
                  <p className="text-gray-400 mb-4">{t('auth.rememberPassword')}</p>
                  <Link
                    to="/drivers/login"
                    className="text-amber-500 font-semibold hover:text-amber-400 transition-colors"
                    data-testid="login-link"
                  >
                    {t('common.backToLogin')} →
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TransporterForgotPasswordPage;
